"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatOnboarding/AskUserQuestionCard";

import { useMemo, useState, type ReactNode } from "react";
import { cn } from "utils";
import type {
  AskUserQuestionAnswer,
  AskUserQuestionArgs,
  AskUserQuestionArgsInput,
  AskUserQuestionItem,
  AskUserQuestionOption,
  AskUserQuestionToolInfo,
} from "./types";
import { normalizeAskUserQuestionArgs } from "./askUserQuestionOptions";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { ospThemes } from "@ospweb/sdk-ui";
import CheckBox from "../CheckBox";
import { trackEvent } from "@utils/tracking";
import { trackOnboardingAnalytics } from "@/src/hooks/onboarding/onboardingAnalytics";
import { Track } from "@/src/lib/tracking";
import { buildOpenReplayLabel } from "@utils/openreplay";

const I18n = I18nUtil.createScoped(Translations);

/** Figma `87676:35436` — 选中行背景（基线 `93137:60353` 不变） */
const ASK_USER_QUESTION_OPTION_SELECTED_CLASS = "bg-background_white_overlay5";

/** 选项区 full-bleed：抵消 Shell `p-4`，选中背景贴卡片左右缘；行内 `px-4` 与标题对齐 */
const ASK_USER_QUESTION_OPTION_LIST_CLASS = "-mx-4";
const ASK_USER_QUESTION_OPTION_ROW_CLASS =
  "flex w-full items-center justify-between gap-[12px] px-4 py-4 enabled:active:bg-background_white_overlay5 has-[:active]:bg-background_white_overlay5";

const AskUserQuestionOptionLabel = ({
  option,
}: {
  option: AskUserQuestionOption;
}) => (
  <span className="flex min-w-0 flex-1 items-center gap-2 body_m">
    <span className="min-w-0">{option.label}</span>
    {/* {option.recommended ? <AskUserQuestionRecommendedBadge /> : null} */}
  </span>
);

export interface AskUserQuestionCardProps {
  /** API 原始 args（options 可为 string 或 { label, recommended }） */
  args: AskUserQuestionArgsInput;
  toolInfo?: AskUserQuestionToolInfo;
  className?: string;
  /** 非最后一条消息且无 result 时禁用交互 */
  disabled?: boolean;
  submitLabel?: string;
  onSubmit?: (answers: AskUserQuestionAnswer[]) => void;
  trackingScene?: string;
}

export interface InteractiveToolCardShellProps {
  title: ReactNode;
  /** 标题行右侧插槽（如 ask_user_question 分页）— Figma `88993:38453` */
  headerTrailing?: ReactNode;
  className?: string;
  titleClassName?: string;
  toolInfo?: AskUserQuestionToolInfo;
  children: ReactNode;
  /** 交互态：自由输入 + Send；查看态不传 footer，外层用 pb-4 补齐底边距 */
  footer?: ReactNode;
}

/**
 * 交互式工具卡片外壳（交互态 / 查看态共用）。
 * Figma 基础容器：圆角 14、背景 background_solid_neutral_primary、标题 18/28 semibold。
 */
export const InteractiveToolCardShell = ({
  title,
  headerTrailing,
  className,
  titleClassName,
  toolInfo,
  children,
  footer,
}: InteractiveToolCardShellProps) => {
  return (
    <div
      className={cn(
        "w-full max-w-[360px] rounded-[14px] h-fit bg-background_solid_neutral_primary p-4 pb-0",
        className,
      )}
      data-tool-name={toolInfo?.toolName}
      data-tool-call-id={toolInfo?.toolCallId}
    >
      <div className={cn("mb-3 flex items-center gap-[10px]", titleClassName)}>
        <h3 className="m-0 min-w-0 flex-1 [word-break:break-word] title_l text-content_primary tracking-[-0.2px]">
          {title}
        </h3>
        {headerTrailing ? (
          <div className="flex shrink-0 items-center">{headerTrailing}</div>
        ) : null}
      </div>
      {children}
      {footer}
    </div>
  );
};

type AskUserQuestionPaginationProps = {
  questionIndex: number;
  questionsCount: number;
  canInteract: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
};

/** Figma `88993:38453` — 标题行右侧 ‹ n/N › */
const AskUserQuestionPagination = ({
  questionIndex,
  questionsCount,
  canInteract,
  onPrevious,
  onNext,
}: AskUserQuestionPaginationProps) => (
  <div
    className="flex items-center gap-4"
    data-testid="ask-user-question-pagination"
  >
    <button
      type="button"
      className="h-4 w-4 rounded-full disabled:opacity-40"
      onClick={onPrevious}
      disabled={!canInteract || questionIndex === 0}
      aria-label="Previous question"
    >
      <OspIcons
        name="chevron-left"
        size={16}
        color={ospThemes.colors.content_primary}
      />
    </button>
    <span className="body_m whitespace-nowrap">
      <span className="text-content_primary">{questionIndex + 1}</span>
      <span className="text-content_secondary">/{questionsCount}</span>
    </span>
    <button
      type="button"
      className="h-4 w-4 rounded-full disabled:opacity-40"
      onClick={onNext}
      disabled={!canInteract || questionIndex >= questionsCount - 1}
      aria-label="Next question"
    >
      <OspIcons
        name="chevron-right"
        size={16}
        color={ospThemes.colors.content_primary}
      />
    </button>
  </div>
);

interface AskUserQuestionCardViewProps {
  className?: string;
  toolInfo?: AskUserQuestionToolInfo;
  question: AskUserQuestionItem;
  questionIndex: number;
  questionsCount: number;
  currentAnswer: AskUserQuestionAnswer;
  onPrevious?: () => void;
  onNext?: () => void;
  onOptionClick: (option: string) => void;
  onFreeInputChange?: (value: string) => void;
  onSubmitClick?: () => void;
  canClickSend: boolean;
  disabled?: boolean;
  submitLabel?: string;
}

const AskUserQuestionCardView = ({
  className,
  toolInfo,
  question,
  questionIndex,
  questionsCount,
  currentAnswer,
  onPrevious,
  onNext,
  onOptionClick,
  onFreeInputChange,
  onSubmitClick,
  canClickSend,
  disabled = false,
  submitLabel = "Send",
}: AskUserQuestionCardViewProps) => {
  const canInteract = !disabled;
  const canSend = canInteract && canClickSend;
  const isMultipleMode = question.selectionMode !== "single";
  const isShowFooter = isMultipleMode || question.allowFreeInput;

  return (
    <InteractiveToolCardShell
      title={question.question}
      headerTrailing={
        questionsCount > 1 ? (
          <AskUserQuestionPagination
            questionIndex={questionIndex}
            questionsCount={questionsCount}
            canInteract={canInteract}
            onPrevious={onPrevious}
            onNext={onNext}
          />
        ) : undefined
      }
      className={className}
      toolInfo={toolInfo}
      footer={
        isShowFooter ? (
          <div className="py-3 flex items-center justify-end gap-[10px]">
            {question.allowFreeInput && (
              <input
                value={currentAnswer.freeInput ?? ""}
                onChange={(event) => onFreeInputChange?.(event.target.value)}
                readOnly={disabled}
                disabled={disabled}
                className="min-w-0 flex-1 bg-transparent body_m text-content_primary placeholder:text-content_quaternary outline-none disabled:cursor-not-allowed disabled:opacity-50 border rounded-2 h-[44px] p-3"
                placeholder="or tell me in your words..."
              />
            )}
            {canSend && (
              <button
                type="button"
                className="min-w-[86px] rounded-2 bg-background_brand_overlay10 p-3 button_m text-content_brand_primary disabled:cursor-not-allowed disabled:text-content_brand_tertiary flex items-center justify-center gap-[2px]"
                disabled={!canSend}
                onClick={onSubmitClick}
                data-openreplay-label={buildOpenReplayLabel(
                  "Onboarding",
                  "Survey",
                  "Continue",
                )}
              >
                {submitLabel}
                <OspIcons
                  name="arrow-right"
                  size={20}
                  color={ospThemes.colors.content_brand_primary}
                />
              </button>
            )}
          </div>
        ) : null
      }
    >
      <div className={ASK_USER_QUESTION_OPTION_LIST_CLASS}>
        {question.options.map((option, optionIndex) => {
          const optionLabel = option.label;
          const selected = currentAnswer.selectedOptions.includes(optionLabel);
          const optionKey = `${optionIndex}-${optionLabel}`;

          if (isMultipleMode) {
            return (
              <div
                key={optionKey}
                data-selected={selected || undefined}
                className={cn(
                  ASK_USER_QUESTION_OPTION_ROW_CLASS,
                  selected && ASK_USER_QUESTION_OPTION_SELECTED_CLASS,
                )}
              >
                <div className="flex min-w-0 flex-1 items-center gap-[12px]">
                  <CheckBox
                    checked={selected}
                    onClick={() => {
                      if (canInteract) {
                        onOptionClick(optionLabel);
                      }
                    }}
                    classNames="shrink-0 rounded-full"
                  />
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left disabled:cursor-not-allowed disabled:opacity-50"
                    data-openreplay-label={buildOpenReplayLabel(
                      "Onboarding",
                      "Survey",
                      "Select",
                    )}
                    disabled={!canInteract}
                    onClick={() => onOptionClick(optionLabel)}
                  >
                    <AskUserQuestionOptionLabel option={option} />
                  </button>
                </div>
                {selected ? (
                  <span className="sr-only">{I18n.t("selected")}</span>
                ) : null}
              </div>
            );
          }

          return (
            <button
              key={optionKey}
              type="button"
              data-selected={selected || undefined}
              className={cn(
                ASK_USER_QUESTION_OPTION_ROW_CLASS,
                "text-left disabled:cursor-not-allowed disabled:opacity-50",
                selected && ASK_USER_QUESTION_OPTION_SELECTED_CLASS,
              )}
              data-openreplay-label={buildOpenReplayLabel(
                "Onboarding",
                "Survey",
                "Select",
              )}
              disabled={!canInteract}
              onClick={() => onOptionClick(optionLabel)}
            >
              <AskUserQuestionOptionLabel option={option} />

              <OspIcons
                name="arrow-right"
                size={16}
                color={ospThemes.colors.content_tertiary}
              />
            </button>
          );
        })}
      </div>
    </InteractiveToolCardShell>
  );
};

const buildEmptyAnswer = (
  question: AskUserQuestionItem,
  questionIndex: number,
): AskUserQuestionAnswer => ({
  questionIndex,
  question: question.question,
  selectedOptions: [],
  freeInput: "",
});

const getDefaultAnswers = (
  args: AskUserQuestionArgs,
): AskUserQuestionAnswer[] => {
  return args.questions.map((question, questionIndex) => {
    return buildEmptyAnswer(question, questionIndex);
  });
};

const toggleMultipleOption = (
  selectedOptions: string[],
  option: string,
): string[] => {
  return selectedOptions.includes(option)
    ? selectedOptions.filter((item) => item !== option)
    : [...selectedOptions, option];
};

const hasCollectedAnswer = (
  question: AskUserQuestionItem,
  answer: AskUserQuestionAnswer,
): boolean => {
  const hasSelectedOptions = answer.selectedOptions.length > 0;
  const hasFreeInput = (answer.freeInput ?? "").trim().length > 0;

  if (!question.allowFreeInput) {
    return hasSelectedOptions;
  }

  return hasSelectedOptions || hasFreeInput;
};

const findFirstUnansweredQuestionIndex = (
  questions: AskUserQuestionItem[],
  answers: AskUserQuestionAnswer[],
): number => {
  return questions.findIndex((question, index) => {
    return !hasCollectedAnswer(question, answers[index]);
  });
};

const isOnboardingTrackingScene = (scene?: string) => scene === "onboarding";

/**
 * GA 侧事件名 —— **保持不动**（存量冻结，规范 §0）。只有前两题有埋点需求。
 *
 * 全部写成字面量而不是 `` `${prefix}_${action}` `` 模板：模板名静态扫不出来，
 * 会在事件总表里塌成 `(dynamic)`，唯一性门禁也查不了。名字一个字没变，只是写法可扫。
 */
const GA_SURVEY_EVENTS: Record<number, { select: string; continue: string }> = {
  0: { select: "ob_survey_q1_select", continue: "ob_survey_q1_continue" },
  1: { select: "ob_survey_q2_select", continue: "ob_survey_q2_continue" },
};

/**
 * 本卡片问卷埋点的**唯一出口**（D2 方案 C · 2026-08-07）。
 *
 * 题号从事件名里收进参数：GA 侧 `ob_survey_q1_select` / `ob_survey_q2_select` 是两个名，
 * 但它们是**同一个动作的第 1、2 题**，不是两件事。OR 侧统一成 `ob_survey_select`
 * + `question_index`，再加一题不会多出事件名。
 *
 * GA 事件名保持旧样 —— 两条通道在这里名字不一致，是收敛的必然代价，对照时用 `question_index`。
 */
function emitSurveyEvent(
  action: "select" | "continue",
  questionIndex: number,
  selectedValues: string[],
  field?: string,
): void {
  const gaEvent = GA_SURVEY_EVENTS[questionIndex]?.[action];
  if (!gaEvent) {
    return;
  }
  // GA 侧原样传数组（存量口径不动）
  trackEvent(gaEvent, {
    ...(field ? { field } : {}),
    selected_values: selectedValues,
  });
  // OR 侧必须扁平标量（规范 §3.2）—— 数组拍平成串，另带个数便于聚合
  const orParams = {
    ...(field ? { field } : {}),
    selected_values: selectedValues.join(","),
    selected_count: selectedValues.length,
    // 1-based，与 GA 的 q1/q2 对齐，便于两边比对
    question_index: questionIndex + 1,
  };
  // 事件名写成字面量而不是 `ob_survey_${action}` —— 模板名静态扫不出来，
  // 会在事件总表里塌成 (dynamic)，唯一性门禁也查不了它。收敛名字的意义就没了。
  if (action === "select") {
    Track.event("ob_survey_select", orParams);
  } else {
    Track.event("ob_survey_continue", orParams);
  }
}

const getSelectedValues = (answer: AskUserQuestionAnswer) => {
  const values = [...answer.selectedOptions];
  const freeInput = answer.freeInput?.trim();

  if (freeInput) {
    values.push(freeInput);
  }

  return values;
};

export const AskUserQuestionCard = ({
  args,
  toolInfo,
  className,
  disabled = false,
  submitLabel = "Send",
  onSubmit,
  trackingScene,
}: AskUserQuestionCardProps) => {
  const normalizedArgs = useMemo(
    () => normalizeAskUserQuestionArgs(args),
    [args],
  );
  const questions = normalizedArgs.questions;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<AskUserQuestionAnswer[]>(() =>
    getDefaultAnswers(normalizedArgs),
  );

  if (questions.length === 0 || answers.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestionIndex];

  const isLastQuestion = currentQuestionIndex >= questions.length - 1;
  const canClickSend =
    currentQuestion.selectionMode === "multiple"
      ? hasCollectedAnswer(currentQuestion, currentAnswer)
      : (currentAnswer.freeInput ?? "").trim().length > 0;

  const withUpdatedAnswer = (
    questionIndex: number,
    updater: (answer: AskUserQuestionAnswer) => AskUserQuestionAnswer,
  ) => {
    setAnswers((previousAnswers) =>
      previousAnswers.map((answer, index) => {
        if (index !== questionIndex) {
          return answer;
        }

        return updater(answer);
      }),
    );
  };

  const onOptionClick = (option: string) => {
    const nextAnswers = answers.map((answer, index) => {
      if (index !== currentQuestionIndex) {
        return answer;
      }

      if (currentQuestion.selectionMode === "single") {
        return {
          ...answer,
          selectedOptions: [option],
        };
      }

      return {
        ...answer,
        selectedOptions: toggleMultipleOption(answer.selectedOptions, option),
      };
    });

    setAnswers(nextAnswers);

    if (isOnboardingTrackingScene(trackingScene)) {
      const nextAnswer = nextAnswers[currentQuestionIndex];
      const selectedValues = getSelectedValues(nextAnswer);

      if (currentQuestionIndex === 0) {
        // GA 发两个名（PRD 名 + legacy 名，存量口径）；OpenReplay 只发 PRD 名 ——
        // 两个名指同一件事，都发就是一次动作两条记录（规范 §5）。
        trackOnboardingAnalytics(
          "ob_hook_select",
          { hook_id: option },
          { event: "ob_funnel_hook_selected" },
        );
      }

      emitSurveyEvent(
        "select",
        currentQuestionIndex,
        selectedValues,
        currentQuestion.question,
      );
    }

    if (currentQuestion.selectionMode === "single") {
      if (isLastQuestion) {
        if (questions.length > 1) {
          const firstUnansweredIndex = findFirstUnansweredQuestionIndex(
            questions,
            nextAnswers,
          );

          if (firstUnansweredIndex >= 0) {
            setCurrentQuestionIndex(firstUnansweredIndex);
            return;
          }
        }

        onSubmit?.(nextAnswers);
      } else {
        setCurrentQuestionIndex((value) =>
          Math.min(questions.length - 1, value + 1),
        );
      }
    }
  };

  const onFreeInputChange = (value: string) => {
    withUpdatedAnswer(currentQuestionIndex, (answer) => ({
      ...answer,
      freeInput: value,
    }));
  };

  const onSubmitClick = () => {
    if (!canClickSend) {
      return;
    }

    if (isLastQuestion) {
      if (questions.length > 1) {
        const firstUnansweredIndex = findFirstUnansweredQuestionIndex(
          questions,
          answers,
        );

        if (firstUnansweredIndex >= 0) {
          setCurrentQuestionIndex(firstUnansweredIndex);
          return;
        }
      }

      if (isOnboardingTrackingScene(trackingScene)) {
        const selectedValues = getSelectedValues(currentAnswer);
        emitSurveyEvent("continue", currentQuestionIndex, selectedValues);

        trackOnboardingAnalytics("ob_funnel_survey_completed", {
          selected_values: answers.flatMap(getSelectedValues),
        });
      }

      onSubmit?.(answers);
      return;
    }

    if (isOnboardingTrackingScene(trackingScene)) {
      const selectedValues = getSelectedValues(currentAnswer);
      emitSurveyEvent("continue", currentQuestionIndex, selectedValues);
    }

    setCurrentQuestionIndex((value) =>
      Math.min(questions.length - 1, value + 1),
    );
  };

  return (
    <AskUserQuestionCardView
      className={className}
      toolInfo={toolInfo}
      question={currentQuestion}
      questionIndex={currentQuestionIndex}
      questionsCount={questions.length}
      currentAnswer={currentAnswer}
      onPrevious={() =>
        setCurrentQuestionIndex((value) => Math.max(0, value - 1))
      }
      onNext={() =>
        setCurrentQuestionIndex((value) =>
          Math.min(questions.length - 1, value + 1),
        )
      }
      onOptionClick={onOptionClick}
      onFreeInputChange={onFreeInputChange}
      onSubmitClick={onSubmitClick}
      canClickSend={canClickSend}
      disabled={disabled}
      submitLabel={submitLabel}
    />
  );
};
